import { api } from '../../../api';
import { BackendQnAParams } from '../../QnAConfigModal/services/types';
import {
  AIMessage,
  AIResponse,
  AIServiceError,
  StreamingAIResponse,
  StreamingAIService,
} from './types';

/**
 * Backend QnA Service - calls the backend /v1/qna/ask endpoint
 * Implements the same interface as GeminiAIService for seamless switching
 */
export class BackendQnAService implements StreamingAIService {
  private timeout: number;
  private overrideParams?: BackendQnAParams;

  constructor(overrideParams?: BackendQnAParams, timeout = 120000) {
    this.timeout = timeout;
    this.overrideParams = overrideParams;
  }

  /**
   * Update backend override parameters
   */
  setOverrideParams(params: BackendQnAParams): void {
    this.overrideParams = params;
  }

  validateConfig(): boolean {
    // Backend service doesn't require client-side configuration validation
    // Authentication is handled via cookies/session by the backend
    // This always returns true for now, but can be extended in the future
    // to validate admin-configurable parameters (e.g., custom endpoints, timeouts, etc.)
    return true;
  }

  getProviderName(): string {
    return 'Backend API';
  }

  /**
   * Generate non-streaming response
   * Falls back to streaming and collects all chunks
   */
  async generateResponse(messages: AIMessage[], context?: string): Promise<AIResponse> {
    let fullContent = '';

    await this.generateStreamingResponse(messages, context, (chunk) => {
      fullContent = chunk.content;
    });

    return {
      content: fullContent,
    };
  }

  /**
   * Generate streaming response from backend SSE endpoint
   */
  async generateStreamingResponse(
    messages: AIMessage[],
    context?: string,
    onChunk?: (chunk: StreamingAIResponse) => void,
  ): Promise<AIResponse> {
    // Extract last user message as the question
    const userMessages = messages.filter((msg) => msg.role === 'user');
    if (userMessages.length === 0) {
      throw new AIServiceError(
        'No question provided',
        'INVALID_REQUEST',
        false,
      );
    }

    const question = userMessages[userMessages.length - 1].content;

    // Build context from previous messages (exclude the last user message and system messages)
    const contextMessages = messages
      .slice(0, -1)
      .filter((msg) => msg.role !== 'system' && msg.content && msg.content.trim() !== '')
      .map((msg) => ({
        role: msg.role,
        content: msg.content,
      }));

    // Prepare request body with admin override parameters
    const requestBody: {
      question: string;
      context?: Array<{ role: string; content: string }>;
      provider?: string;
      model?: string;
      maxTokens?: number;
      temperature?: number;
      documentId?: string;
    } = {
      question,
    };

    if (contextMessages.length > 0) {
      requestBody.context = contextMessages;
    }

    // Add backend override parameters if provided
    if (this.overrideParams) {
      if (this.overrideParams.provider) {
        requestBody.provider = this.overrideParams.provider;
      }
      if (this.overrideParams.model) {
        requestBody.model = this.overrideParams.model;
      }
      if (this.overrideParams.maxTokens !== undefined) {
        requestBody.maxTokens = this.overrideParams.maxTokens;
      }
      if (this.overrideParams.temperature !== undefined) {
        requestBody.temperature = this.overrideParams.temperature;
      }
      if (this.overrideParams.documentId) {
        requestBody.documentId = this.overrideParams.documentId;
      }
    }

    // Call backend API with fetch for SSE streaming
    const baseURL = api.defaults.baseURL || '';
    const url = `${baseURL}v1/qna/ask`;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Send cookies for authentication
        body: JSON.stringify(requestBody),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.error || errorData.message || 'Backend API error';

        throw new AIServiceError(
          errorMessage,
          response.status === 429 ? 'RATE_LIMIT' : 'API_ERROR',
          response.status >= 500 || response.status === 429,
        );
      }

      // Check if response is SSE
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('text/event-stream')) {
        throw new AIServiceError(
          'Expected SSE stream from backend',
          'INVALID_RESPONSE',
          false,
        );
      }

      // Process SSE stream
      if (!response.body) {
        throw new AIServiceError(
          'No response body from backend',
          'STREAM_ERROR',
          true,
        );
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullContent = '';

      const processStream = (): Promise<void> => {
        let buffer = '';

        const readChunk = (): Promise<void> => reader.read().then(({ done, value }) => {
          if (done) {
            return Promise.resolve();
          }

          const chunk = decoder.decode(value, { stream: true });
          buffer += chunk;

          // Process complete SSE messages (lines starting with "data: ")
          const lines = buffer.split('\n');
          buffer = lines.pop() || ''; // Keep incomplete line in buffer

          lines.forEach((line) => {
            if (line.startsWith('data: ')) {
              const dataStr = line.substring(6); // Remove "data: " prefix

              try {
                const data = JSON.parse(dataStr);

                // Check for error
                if (data.error) {
                  // Log full error details for developers
                  // eslint-disable-next-line no-console
                  console.error('[BackendQnAService] AI Error:', {
                    error: data.error,
                    message: data.message,
                    timestamp: new Date().toISOString(),
                  });

                  throw new AIServiceError(
                    data.message || data.error,
                    'API_ERROR',
                    false,
                  );
                }

                // Check if done
                if (data.done) {
                  if (onChunk) {
                    onChunk({
                      content: fullContent,
                      isComplete: true,
                    });
                  }
                  return;
                }

                // Accumulate content
                if (data.content) {
                  fullContent += data.content;

                  if (onChunk) {
                    onChunk({
                      content: fullContent,
                      isComplete: false,
                    });
                  }
                }
              } catch (parseError) {
                // Re-throw AIServiceError to propagate it to the caller
                if (parseError instanceof AIServiceError) {
                  throw parseError;
                }
                // Skip invalid JSON lines (actual parse errors)
                // eslint-disable-next-line no-console
                console.warn('Failed to parse SSE data:', dataStr);
              }
            }
          });

          return readChunk();
        });

        return readChunk();
      };

      try {
        await processStream();
      } finally {
        reader.releaseLock();
      }

      return {
        content: fullContent,
      };
    } catch (error) {
      clearTimeout(timeoutId);

      if (error instanceof Error && error.name === 'AbortError') {
        throw new AIServiceError('Request timeout', 'TIMEOUT', true);
      }

      if (error instanceof AIServiceError) {
        throw error;
      }

      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      throw new AIServiceError(
        `Backend API error: ${errorMessage}`,
        'NETWORK_ERROR',
        true,
      );
    }
  }
}
